// ============================================
// Creator Editor - Events Module (ES6)
// 탭전환, 이벤트 바인딩, 버튼 상태
// ============================================

import { getIntroBlocks, getInitialEditorState, getExampleDialogues, getCurrentEditorData } from '../state.js';
import { renderIntroPreview } from '../intro-blocks.js';
import { hasEditorStateChanged } from './form.js';
import { getVoicePreset } from '../../config.js';
import { showToast } from '../../utils.js';

// ============================================
// 필드 설정
// ============================================

// 글자 수 카운터가 있는 필드들
const COUNTER_FIELDS = [
  { input: 'editorCharName', counter: 'charNameCount' },
  { input: 'editorExperience', counter: 'charExperienceCount' },
  { input: 'editorCharPrompt', counter: 'charPromptCount' },
  { input: 'editorCharAdditionalPrompt', counter: 'charAdditionalPromptCount' }
  // editorCharDescription는 커스텀 설명 에디터에서 별도 관리
];

// 필수 필드들 (커스텀 설명은 선택 필드)
const REQUIRED_FIELDS = [
  'editorCharName',
  'editorCharGender',
  'editorCharPrompt',
  'editorTargetAudience',
  'editorShortIntro'
];

// 탭별 필수 필드
const TAB_REQUIRED_FIELDS = {
  info: ['editorCharName', 'editorCharGender', 'editorCharPrompt'],
  intro: [], // 인트로 블록 체크는 별도 로직
  description: ['editorTargetAudience', 'editorShortIntro'], // 커스텀 설명은 선택
  example: [], // 대화 예시는 선택
  advanced: [] // 추가 프롬프트는 선택
};

// ============================================
// 에디터 탭 전환
// ============================================

export function switchEditorTab(tabName) {
  document.querySelectorAll('.editor-tab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.editorTab === tabName);
  });

  document.querySelectorAll('.editor-tab-content').forEach(content => {
    content.classList.remove('active');
  });

  const tabContent = document.getElementById(`editorTab${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);
  if (tabContent) {
    tabContent.classList.add('active');
  }
}

// ============================================
// 입력 이벤트 바인딩
// ============================================

export function bindEditorEvents() {
  // 글자수 카운터 이벤트
  COUNTER_FIELDS.forEach(({ input, counter }) => {
    const inputEl = document.getElementById(input);
    const counterEl = document.getElementById(counter);
    if (inputEl && counterEl) {
      inputEl.addEventListener('input', () => {
        counterEl.textContent = inputEl.value.length;
        updateFieldValidationState(input);
        updateTabCheckStatus();
        updatePublishButtonState();
      });
    }
  });

  // 인트로 타입 탭 클릭 이벤트
  document.querySelectorAll('.intro-type-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      if (window.CreatorModule) {
        window.CreatorModule.setIntroType(btn.dataset.type);
      }
    });
  });

  // 인트로 입력창 Enter 키 처리
  const introInput = document.getElementById('introComposerInput');
  if (introInput) {
    introInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (window.CreatorModule) {
          window.CreatorModule.addIntroBlock();
        }
      }
    });
  }

  // 캐릭터 이름 변경시 인트로 미리보기 업데이트
  const charNameEl = document.getElementById('editorCharName');
  if (charNameEl) {
    charNameEl.addEventListener('input', renderIntroPreview);
  }

  // 한 줄 소개 입력 이벤트
  const shortIntroEl = document.getElementById('editorShortIntro');
  if (shortIntroEl) {
    shortIntroEl.addEventListener('input', () => {
      updateFieldValidationState('editorShortIntro');
      updateTabCheckStatus();
      updatePublishButtonState();
    });
  }

  // 타겟 선택 이벤트
  const targetEl = document.getElementById('editorTargetAudience');
  if (targetEl) {
    targetEl.addEventListener('change', () => {
      updateFieldValidationState('editorTargetAudience');
      updateTabCheckStatus();
      updatePublishButtonState();
    });
  }

  // 성별 선택 이벤트
  const genderEl = document.getElementById('editorCharGender');
  if (genderEl) {
    genderEl.addEventListener('change', () => {
      updateFieldValidationState('editorCharGender');
      updateTabCheckStatus();
      updatePublishButtonState();
    });
  }

  // 경력 입력 이벤트 (선택 필드)
  const experienceEl = document.getElementById('editorExperience');
  if (experienceEl) {
    experienceEl.addEventListener('input', () => {
      const counterEl = document.getElementById('charExperienceCount');
      if (counterEl) counterEl.textContent = experienceEl.value.length;
    });
  }

  // 음성 미리듣기 버튼 이벤트
  const voicePreviewBtn = document.querySelector('[data-action="previewVoice"]');
  if (voicePreviewBtn) {
    voicePreviewBtn.addEventListener('click', previewVoice);
  }

  // 공개 설정 라디오 버튼 이벤트
  document.querySelectorAll('input[name="editorVisibility"]').forEach(radio => {
    radio.addEventListener('change', updatePublishButtonState);
  });

  // 체크박스 변경 이벤트
  const checkboxFields = ['editorSafetyFilter', 'editorShowThoughts'];
  checkboxFields.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('change', updatePublishButtonState);
    }
  });

  // 추가 프롬프트 토글 이벤트
  const additionalPromptToggle = document.getElementById('editorAdditionalPromptToggle');
  const additionalPromptContent = document.getElementById('additionalPromptContent');
  if (additionalPromptToggle && additionalPromptContent) {
    additionalPromptToggle.addEventListener('change', () => {
      additionalPromptContent.style.display = additionalPromptToggle.checked ? 'block' : 'none';
    });
  }

  // AI 다듬기 버튼 이벤트
  const aiRefineBtn = document.getElementById('aiRefineBtn');
  if (aiRefineBtn) {
    aiRefineBtn.addEventListener('click', async () => {
      const { showConfirm } = await import('../../shared/confirm-modal.js');
      const confirmed = await showConfirm('작성된 설명을 바탕으로 AI가 설명을 다듬어줘요. 진행할까요?');
      if (confirmed) {
        const { refineCharacterPrompt } = await import('../autofill/refine.js');
        await refineCharacterPrompt();
      }
    });
  }

  // 자동 생성 버튼 이벤트 (소개 탭)
  const autoGenerateDescBtn = document.getElementById('autoGenerateDescBtn');
  if (autoGenerateDescBtn) {
    autoGenerateDescBtn.addEventListener('click', async () => {
      const { showConfirm } = await import('../../shared/confirm-modal.js');
      const confirmed = await showConfirm('작성된 점술가 정보를 바탕으로 점술가 설명을 자동 생성해요. 진행할까요?');
      if (confirmed) {
        const { generateCharacterDescription } = await import('../autofill/refine.js');
        await generateCharacterDescription();
      }
    });
  }

  // 초기 상태 업데이트
  updateCharCounts();
  updateAllFieldValidationStates();
  updateTabCheckStatus();
  updatePublishButtonState();
}

// ============================================
// 필드 검증 상태 업데이트
// ============================================

export function updateFieldValidationState(fieldId) {
  const el = document.getElementById(fieldId);
  if (!el) return;

  const isRequired = REQUIRED_FIELDS.includes(fieldId);
  let hasValue = false;

  if (el.tagName === 'SELECT') {
    hasValue = el.value && el.value !== '';
  } else {
    hasValue = el.value?.trim().length > 0;
  }

  // 필드 상태 표시 업데이트 (new pattern)
  const fieldStatus = document.querySelector(`.field-status[data-field="${fieldId}"]`);
  if (fieldStatus) {
    const fieldCheck = fieldStatus.querySelector('.field-check');
    if (fieldCheck) {
      fieldCheck.classList.toggle('hidden', !(isRequired && hasValue));
    }
  }
}

export function updateAllFieldValidationStates() {
  REQUIRED_FIELDS.forEach(fieldId => {
    updateFieldValidationState(fieldId);
  });
  updateImageValidationState();
}

export function updateImageValidationState() {
  const editorData = getCurrentEditorData();
  const hasImages = editorData.images && editorData.images.length > 0;

  const fieldStatus = document.querySelector('.field-status[data-field="editorImages"]');
  if (fieldStatus) {
    const fieldCheck = fieldStatus.querySelector('.field-check');
    if (fieldCheck) {
      fieldCheck.classList.toggle('hidden', !hasImages);
    }
  }
}

// ============================================
// 탭 체크 상태 업데이트
// ============================================

export function updateTabCheckStatus() {
  // 정보 탭
  const infoComplete = TAB_REQUIRED_FIELDS.info.every(id => {
    const el = document.getElementById(id);
    return el?.value?.trim().length > 0;
  });
  updateTabCheck('info', infoComplete);

  // 인트로 탭
  const introComplete = getIntroBlocks().length > 0;
  updateTabCheck('intro', introComplete);

  // 소개 탭
  const descriptionComplete = TAB_REQUIRED_FIELDS.description.every(id => {
    const el = document.getElementById(id);
    if (el?.tagName === 'SELECT') {
      return el.value && el.value !== '';
    }
    return el?.value?.trim().length > 0;
  });
  updateTabCheck('description', descriptionComplete);
}

function updateTabCheck(tabName, isComplete) {
  const tab = document.querySelector(`.editor-tab[data-editor-tab="${tabName}"]`);
  if (!tab) return;

  const check = tab.querySelector('.tab-check');
  if (check) {
    check.classList.toggle('hidden', !isComplete);
  }
}

// ============================================
// 등록 버튼 상태
// ============================================

export function updatePublishButtonState() {
  const publishBtn = document.getElementById('editorPublishBtn');
  if (!publishBtn) return;

  // 필수 필드 검증
  const charName = document.getElementById('editorCharName')?.value?.trim();
  const charGender = document.getElementById('editorCharGender')?.value;
  const charPrompt = document.getElementById('editorCharPrompt')?.value?.trim();
  const target = document.getElementById('editorTargetAudience')?.value;
  const shortIntro = document.getElementById('editorShortIntro')?.value?.trim();
  const hasIntro = getIntroBlocks().length > 0;
  const editorData = getCurrentEditorData();
  const hasImages = editorData.images && editorData.images.length > 0;

  // 커스텀 설명(description)은 선택 필드이므로 필수 조건에서 제외
  const isValid = charName && charGender && charPrompt &&
                  target && shortIntro && hasIntro && hasImages;

  if (getInitialEditorState()) {
    const hasChanges = hasEditorStateChanged();
    publishBtn.disabled = !(isValid && hasChanges);
  } else {
    publishBtn.disabled = !isValid;
  }
}

// ============================================
// 글자 수 카운터 업데이트
// ============================================

export function updateCharCounts() {
  COUNTER_FIELDS.forEach(({ input, counter }) => {
    const inputEl = document.getElementById(input);
    const counterEl = document.getElementById(counter);
    if (inputEl && counterEl) {
      counterEl.textContent = inputEl.value.length;
    }
  });

  // 대화 예시 총 글자 수 업데이트
  updateExampleDialogueTotalCount();
}

export function updateExampleDialogueTotalCount() {
  const countEl = document.getElementById('exampleDialogueTotalCount');
  if (!countEl) return;

  const dialogues = getExampleDialogues();
  let totalChars = 0;

  dialogues.forEach(dialogue => {
    if (dialogue.blocks) {
      dialogue.blocks.forEach(block => {
        totalChars += (block.content || '').length;
      });
    }
  });

  countEl.textContent = totalChars;
}

// ============================================
// 이미지 생성 버튼 상태
// ============================================

export function updateImageGenButtonState() {
  const btn = document.getElementById('editorImageGenBtn');
  if (!btn) return;

  // 캐릭터 프롬프트가 있어야 이미지 생성 가능
  const prompt = document.getElementById('editorCharPrompt')?.value?.trim();
  btn.disabled = !prompt;
}

// ============================================
// 공개 설정 값 가져오기
// ============================================

export function getVisibilityValue() {
  const checkedRadio = document.querySelector('input[name="editorVisibility"]:checked');
  return checkedRadio?.value || 'public';
}

export function setVisibilityValue(value) {
  const radio = document.querySelector(`input[name="editorVisibility"][value="${value}"]`);
  if (radio) {
    radio.checked = true;
  }
}

// ============================================
// 음성 미리듣기
// ============================================

let previewAudio = null;

async function previewVoice() {
  const voiceSelect = document.getElementById('editorVoiceId');
  const voiceId = voiceSelect?.value;

  if (!voiceId) {
    showToast('음성을 선택해주세요');
    return;
  }

  const preset = getVoicePreset(voiceId);
  if (!preset) {
    showToast('음성 정보를 찾을 수 없습니다');
    return;
  }

  // 이전 재생 중지
  if (previewAudio) {
    previewAudio.pause();
    previewAudio = null;
  }

  const btn = document.querySelector('[data-action="previewVoice"]');

  try {
    // 버튼 로딩 상태
    if (btn) {
      btn.disabled = true;
      btn.classList.add('loading');
    }

    // TTS 모듈에서 미리듣기 함수 동적 import
    const { previewTTS } = await import('../../chat/tts/api.js');
    const sampleText = '안녕하세요, 저는 당신의 운명을 읽어드리는 점술가입니다. 오늘 무엇이 궁금하신가요?';

    previewAudio = await previewTTS(sampleText, voiceId);

    if (previewAudio) {
      previewAudio.play();
      previewAudio.onended = () => {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('loading');
        }
      };
    }
  } catch (error) {
    console.error('[previewVoice] 미리듣기 실패:', error);
    showToast('미리듣기에 실패했습니다');
  } finally {
    if (btn && !previewAudio) {
      btn.disabled = false;
      btn.classList.remove('loading');
    }
  }
}
